import { CountryCode, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'
import type { RegisterOptions } from 'react-hook-form'

const nameRegexp = new RegExp(/[^\p{L}']/u)

export const getAge = (DOB: string): number | null => {
  if (!/^\d{2}-\d{2}-\d{4}$/.test(DOB)) {
    return null
  }

  const [day, month, year] = DOB.split('-').map(Number)
  if (isNaN(day) || isNaN(month) || isNaN(year)) {
    return null
  }

  const birthDate = new Date(Date.UTC(year, month - 1, day))
  const today = new Date()
  if (birthDate.getUTCFullYear() !== year || birthDate.getUTCMonth() !== month - 1 || birthDate.getUTCDate() !== day) {
    return null
  }

  let age = today.getUTCFullYear() - birthDate.getUTCFullYear()
  const m = today.getUTCMonth() - birthDate.getUTCMonth()
  if (m < 0 || (m === 0 && today.getUTCDate() < birthDate.getUTCDate())) {
    age--
  }

  return age
}

export const isValidDate = (text: string): boolean => {
  if (!/^\d{2}-\d{2}-\d{4}$/.test(text)) {
    return false
  }

  const [d, m, y] = text.split('-').map(Number)
  if (isNaN(d) || isNaN(m) || isNaN(y)) {
    return false
  }

  const date = new Date(Date.UTC(y, m - 1, d))
  return date.getUTCDate() === d && date.getUTCMonth() === m - 1 && date.getUTCFullYear() === y
}

export const isValidName = (str: string) => !nameRegexp.test(str) && str.length >= 2

export const isValidPhone = (str: string) => str.startsWith('07') && (str.length === 11 || str.length === 10)

export const isDialCodeOnly = (value: string) => !value || /^\+\d{1,3}\s*$/.test(value.trim())

// Map libphonenumber-js error codes to user-friendly messages
const getPhoneValidationErrorMessage = (error: string, countryCode?: string): string => {
  const countryName = countryCode?.toUpperCase() || 'the selected country'

  // Common libphonenumber-js error codes and their user-friendly messages
  const errorMessages: Record<string, string> = {
    TOO_SHORT: 'Phone number too short',
    TOO_LONG: 'Phone number too long',
    INVALID_COUNTRY: 'Invalid country selected',
    INVALID_COUNTRY_CODE: 'Country not supported',
    NOT_A_NUMBER: 'Numbers only please',
    INVALID_LENGTH: `Invalid length for ${countryName}`,
    INVALID_FORMAT: `Invalid format for ${countryName}`,
    NO_NATIONAL_PREFIX: `Area code required for ${countryName}`,
    INVALID_EXTENSION: 'Invalid extension',
    NOT_AN_INTERNATIONAL_NUMBER: 'International format required',
    PHONE_NUMBER_TOO_SHORT_AFTER_IDD: 'Number incomplete',
    PHONE_NUMBER_TOO_LONG_AFTER_IDD: 'Number too long',
    COULD_NOT_INTERPRET_NUMBER: 'Invalid number format',
    LEADING_ZERO_POSSIBLE: `Missing digits for ${countryName}`
  }

  // Check if the error message contains any of the known error codes
  for (const [code, message] of Object.entries(errorMessages)) {
    if (error.includes(code)) {
      return message
    }
  }

  // For any other validation errors, provide a generic helpful message
  if (error.toLowerCase().includes('invalid')) {
    return `Invalid number for ${countryName}`
  }

  if (error.toLowerCase().includes('format')) {
    return `Invalid format for ${countryName}`
  }

  // Fallback for unknown errors
  return 'Invalid phone number'
}

// Dynamic phone validation based on country
export const validateInternationalPhone = (phoneNumber: string, countryCode?: string): string | boolean => {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return true // Allow empty values, let required validation handle it
  }

  try {
    // If no country code provided, try to parse as international number
    if (!countryCode || typeof countryCode !== 'string') {
      const isValid = isValidPhoneNumber(phoneNumber)
      return isValid || 'Invalid international number'
    }

    // Normalize and validate country code
    const normalizedCountryCode = countryCode.toLowerCase()

    // Validate that the country code is a valid 2-letter ISO code
    if (!/^[a-z]{2}$/i.test(normalizedCountryCode)) {
      return 'Invalid country selected'
    }

    // Parse with specific country
    const phoneNumberParsed = parsePhoneNumber(phoneNumber, normalizedCountryCode.toUpperCase() as CountryCode)

    if (!phoneNumberParsed) {
      return `Invalid number for ${countryCode.toUpperCase()}`
    }

    const isValid = phoneNumberParsed.isValid()
    return isValid || `Invalid number for ${countryCode.toUpperCase()}`
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    return getPhoneValidationErrorMessage(errorMessage, countryCode)
  }
}

export const stripTags = (str?: string) => str?.replace(/(<([^>]+)>)/gi, '')

export const emailValidation: RegisterOptions = {
  required: true,
  pattern: {
    value: /\S+@\S+\.\S+/,
    message: 'Email is invalid'
  }
}

export const passwordValidation: RegisterOptions = {
  required: true,
  minLength: {
    value: 8,
    message: 'Password must be at least 8 characters long'
  },
  validate: {
    upperCase: (value: string) => /[A-Z]/.test(value) || 'Password must contain at least one uppercase letter',
    lowerCase: (value: string) => /[a-z]/.test(value) || 'Password must contain at least one lowercase letter',
    number: (value: string) => /[0-9]/.test(value) || 'Password must contain at least one number'
  }
}

export const questionValidations: Record<
  string,
  {
    validate: (value: string) => boolean
    errorMessage: string
    placeholder: string
    label: string
  }
> = {
  blood_pressure: {
    validate: (value: string) => /^\d{1,3}\/\d{1,3}$/.test(value),
    errorMessage: 'Please enter blood pressure as Systolic/Diastolic (e.g. 120/80)',
    placeholder: 'e.g. 120/80',
    label: 'Enter Blood Pressure (Systolic/Diastolic)'
  }
}

export function escapeHTML(text: string) {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;')
}

const UK_POSTCODE_REGEX = /^[a-z]{1,2}\d[a-z\d]?\s*\d[a-z]{2}$/i

export const isValidUKPostcode = (postcode: string): boolean => {
  if (!postcode || typeof postcode !== 'string') {
    return false
  }

  const trimmed = postcode.trim()
  if (trimmed.length === 0) {
    return false
  }

  return UK_POSTCODE_REGEX.test(trimmed)
}

export const formatUKPostcode = (postcode: string): string => {
  if (!postcode) return postcode

  const cleaned = postcode.replace(/\s+/g, '').toUpperCase()

  if (cleaned.length >= 5) {
    const outward = cleaned.slice(0, -3)
    const inward = cleaned.slice(-3)
    return `${outward} ${inward}`
  }

  return cleaned
}

export const validateUKPostcodeExists = async (postcode: string): Promise<boolean> => {
  if (!postcode || !isValidUKPostcode(postcode)) {
    return false
  }

  try {
    const cleanPostcode = postcode.replace(/\s+/g, '').toUpperCase()
    const response = await fetch(`/api/postcode-lookup?postcode=${encodeURIComponent(cleanPostcode)}`)

    if (!response.ok) {
      // If API fails, don't block the user - fall back to format validation
      return true
    }

    const data = await response.json()
    // exists: true = API confirmed postcode exists
    // exists: false = API confirmed postcode doesn't exist
    // exists: null = API unavailable, fall back to format validation
    if (data.exists === null) return true
    return data.exists
  } catch (error) {
    // If API fails, don't block the user - fall back to format validation
    return true
  }
}

export const postcodeValidation = {
  required: 'Postcode is required',
  validate: {
    validFormat: (value: string) => isValidUKPostcode(value) || 'Please enter a valid UK postcode',
    exists: async (value: string) => {
      if (!value || !isValidUKPostcode(value)) return true // Let format validation handle this
      const exists = await validateUKPostcodeExists(value)
      return exists || 'This postcode does not exist'
    }
  }
}
